import { useIDFilter } from '@src/hooks/filters/useIDFilter';
import { useRollupsFilter } from '@src/hooks/filters/useRollupsFilter';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import {
  EMPTY_OBJECT,
  FilterClauseType,
  compactArray,
  filterExistsAndExcludes,
  getTypedKeys,
  removeEmptyFilters,
  useCurrenciesFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type FilterClause,
  type FilterableProperty,
  type IHedgePositionStatus,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import type { HedgePositionStatusFilter } from './types';

export const useHedgePositionStatusFilter = ({
  initialFilter = EMPTY_OBJECT,
  saveFilter,
}: {
  initialFilter?: HedgePositionStatusFilter;
  saveFilter: (newFilter: any) => void;
}) => {
  const [filter, setFilter] = useState(initialFilter);

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;
      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const { subAccountsByID } = useSubAccounts();

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<IHedgePositionStatus>>(
    row => {
      const data = row.data;

      if (filterExistsAndExcludes(filter, 'HedgeControlStatus', data, 'HedgeControlStatus')) {
        return false;
      }

      if (filter.AccountID && data) {
        const subAccountName = subAccountsByID.get(data.AccountID)?.Name;
        if (
          subAccountName &&
          filterExistsAndExcludes(filter, 'AccountID', { AccountID: subAccountName }, 'AccountID')
        ) {
          return false;
        }
      }
      if (
        filterExistsAndExcludes(filter, 'Asset', data, 'Asset') &&
        filterExistsAndExcludes(filter, 'Asset', data, 'TargetAsset')
      ) {
        return false;
      }

      if (filterExistsAndExcludes(filter, 'HedgeRuleID', data, 'HedgeRuleID')) {
        return false;
      }

      return true;
    },
    [filter, subAccountsByID]
  );

  const currencyFilter = useCurrenciesFilter();
  const rollupsFilter = useRollupsFilter();
  const idFilter = useIDFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      { ...currencyFilter, key: 'Asset', label: 'Asset' },
      { ...rollupsFilter, key: 'AccountID', label: 'Position Rollup' },
      { ...idFilter, key: 'HedgeRuleID', label: 'Hedge Rule ID' },
    ],
    [currencyFilter, rollupsFilter, idFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      getTypedKeys(filter).forEach(key => {
        const filterValue = filter[key];
        const selections = Array.isArray(filterValue)
          ? filterValue.map(x => x.toString())
          : compactArray([filterValue]);
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections,
        });
      });
    }
    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters({
          ...curr,
          HedgeControlStatus: initialFilter.HedgeControlStatus ? initialFilter.HedgeControlStatus : undefined,
          HedgeRuleID: filterClausesByPropertyKey.get('HedgeRuleID')?.selections,
          AccountID: filterClausesByPropertyKey.get('AccountID')?.selections,
          Asset: filterClausesByPropertyKey.get('Asset')?.selections,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, initialFilter.HedgeControlStatus]
  );

  return {
    filter,
    filterableProperties,
    initialFilterClauses,
    changeFilter,
    clientSideFilter,
    handleFilterClausesChanged,
  };
};
